import type { AronReasoningMode, AronWorkMode } from "../types";

/**
 * Nazwy trybów i ocena jakości — warstwa prezentacji nad tym, co daje backend.
 *
 * Backendowe etykiety (services/modes.py) opisywały KONFIGURACJĘ ("Standardowy",
 * "Głęboki"), a nie to, po co się dany tryb bierze — przy ośmiu pozycjach nikt nie
 * pamiętał, czym „Szybki" różni się od „Ekonomicznego". Tutaj każdy tryb dostaje
 * nazwę mówiącą, do czego jest, plus ocenę. Klucz to wartość enuma, nie etykieta:
 * gdy backend zmieni tekst, nazwa się nie rozjedzie.
 *
 * OCENA (0–5, co 0,5) to NASZA ocena jakości odpowiedzi na trudnych zadaniach —
 * liczby, finanse, wielokrokowa analiza — a nie benchmark dostawcy. Wynika z
 * trzech rzeczy, które o modelu wiadomo z konfiguracji: półki modelu (Opus >
 * Sonnet > Haiku), tego czy myślenie jest włączone, i poziomu effort. Modele
 * zewnętrzne (DeepSeek) trzymają się niżej, bo nie mają serwerowych narzędzi —
 * ani wykresów, ani wyszukiwania.
 */
interface ModeMeta {
  label: string;
  /** 0–5, skok 0,5. */
  rating: number;
  /** Podmieniamy tylko tam, gdzie backendowy tekst odwołuje się do starych nazw. */
  hint?: string;
  /**
   * Konkretne zadania „do czego to wziąć". Ocena i jednozdaniowa podpowiedź mówią,
   * jaki tryb jest MOCNY — a nie, do czego się nadaje; przy dziewięciu pozycjach
   * ludzie i tak brali domyślny albo najdroższy. Trzy przykłady z własnego podwórka
   * rozstrzygają wybór szybciej niż jakikolwiek opis parametrów.
   */
  examples?: string[];
}

export const MODE_META: Partial<Record<AronReasoningMode, ModeMeta>> = {
  express: {
    label: "Oszczędny",
    rating: 2.5,
    examples: [
      "Jaki jest status zamówienia ZE-2026/154?",
      "Kto jest koordynatorem przedsięwzięcia w Kleczy?",
      "Wypisz otwarte zgłoszenia z tego tygodnia",
    ],
  },
  fast: {
    label: "Lekki",
    rating: 3.5,
    examples: [
      "Zestaw faktury od tego dostawcy z ostatniego kwartału",
      "Napisz maila do podwykonawcy na podstawie ustaleń z zamówienia",
      "Streść, co się działo w tym przedsięwzięciu od maja",
    ],
  },
  luna: {
    label: "Błyskawiczny",
    rating: 3,
    examples: [
      "Ile mam otwartych zamówień w tym miesiącu?",
      "Streść ten protokół (PDF) i wypisz ustalenia",
      "Wyciągnij dane kontaktowe z tego skanu",
    ],
    hint:
      "Najtańszy tryb, który czyta załączniki, i najszybszy w odpowiedzi — do pytań " +
      "o dane i prostych raportów. Do liczb, finansów i trudnych analiz przełącz się " +
      "na Domyślny lub Dokładny.",
  },
  standard: {
    label: "Domyślny",
    rating: 4.5,
    hint: "Adaptacyjne myślenie, dobra równowaga jakości i kosztu — do liczb i analiz.",
    examples: [
      "Sprawdź, czy pozycje z POCK zgadzają się z fakturą",
      "Porównaj oferty z postępowania i wskaż różnice",
      "Przygotuj wykaz działek do wniosku o wypis",
    ],
  },
  deep: {
    label: "Dokładny",
    rating: 5,
    examples: [
      "Przeanalizuj 40 oświadczeń i zbuduj z nich arkusz",
      "Znajdź niespójności w kosztorysie na 300 pozycji",
      "Rozpisz ryzyka tej umowy ramowej punkt po punkcie",
    ],
  },
  deepseek: {
    label: "Zewnętrzny tani",
    rating: 2,
    examples: [
      "Przepisz 200 wierszy do jednolitego formatu",
      "Wyciągnij numery działek z tej listy",
      "Masowa, powtarzalna robota na tekście (serwery poza GK)",
    ],
  },
  deepseek_pro: {
    label: "Zewnętrzny mocniejszy",
    rating: 3,
    examples: [
      "Przelicz i sprawdź sumy w dużym zestawieniu",
      "Uporządkuj wykaz z setek pozycji i policz podsumowania",
      "To samo co tańszy DeepSeek, gdy liczby muszą się zgadzać",
    ],
  },
  gpt5_mini: {
    label: "Mini z kodem",
    rating: 2.5,
    examples: [
      "Policz to zestawienie i pokaż wynik na wykresie",
      "Przerób wklejone dane skryptem (bez ruszania bazy)",
      "Szybkie przeliczenia na danych z pliku",
    ],
  },
  astra: {
    label: "Flagowy OpenAI",
    rating: 5,
    examples: [
      "Przeczytaj całą SIWZ i wypisz wymagania dla oferty",
      "Przejrzyj wielosetstronicową dokumentację naraz (okno 1M)",
      "Trudna analiza, gdy pomyłka kosztuje więcej niż tura",
    ],
    hint:
      "GPT-6 Astra — najmocniejszy model OpenAI, okno 1M tokenów, reasoning + " +
      "uruchamianie kodu. Najdroższy tryb ($10 / $50 za 1M) — do trudnych analiz, " +
      "nie do codziennych pytań o dane.",
  },
};

/** Nazwa trybu do pokazania — nasza, a gdy tryb jest nieznany, backendowa. */
export function modeLabel(mode: Pick<AronWorkMode, "value" | "label">): string {
  return MODE_META[mode.value]?.label ?? mode.label;
}

export function modeHint(mode: Pick<AronWorkMode, "value" | "hint">): string {
  return MODE_META[mode.value]?.hint ?? mode.hint;
}

/** Przykładowe zadania „do czego to wziąć" — pusta lista, gdy trybu nie opisano. */
export function modeExamples(mode: Pick<AronWorkMode, "value">): string[] {
  return MODE_META[mode.value]?.examples ?? [];
}

export function modeRating(mode: Pick<AronWorkMode, "value">): number | null {
  return MODE_META[mode.value]?.rating ?? null;
}

const STAR =
  "M12 2.6l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.4 6.2 20.5l1.1-6.5L2.6 9.4l6.5-.9z";

function StarRow({ size, className }: { size: number; className: string }) {
  return (
    <span className={`flex w-max gap-px ${className}`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <svg key={i} width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d={STAR} />
        </svg>
      ))}
    </span>
  );
}

/** Gwiazdki z połówkami: pełny rząd przycięty do procentu oceny leży na szarym.
 *  Bez clipPath, bo wydruk renderuje się statycznie i identyfikatory musiałyby
 *  być unikalne w całym dokumencie. */
export function ModeStars({ rating, size = 12 }: { rating: number; size?: number }) {
  const pct = Math.max(0, Math.min(100, (rating / 5) * 100));
  return (
    <span
      className="relative inline-flex leading-none"
      title={`Nasza ocena jakości odpowiedzi: ${rating.toFixed(1).replace(".", ",")} / 5`}
      aria-label={`Ocena ${rating} na 5`}
    >
      <StarRow size={size} className="text-gray-300 dark:text-gray-600" />
      <span
        className="absolute top-0 left-0 flex overflow-hidden"
        style={{ width: `${pct}%` }}
      >
        <StarRow size={size} className="text-amber-500" />
      </span>
    </span>
  );
}
